import React, { useState, useEffect, useMemo } from 'react';
import {
  Settings, Plus, Edit2, Trash2, X, Save, Percent, DollarSign,
  Palette, DoorOpen, Sliders, Lightbulb, Box, Layers, Filter, CheckCircle2, AlertCircle, Search, ChevronDown, Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import budgetSheetOptionsService from '../services/budgetSheetOptionsService';
import ConfirmModal from './ConfirmModal';
import { FICHA_TECNICA_PRESET_OPTIONS } from '../constants/fichaTecnicaPresets';

const BudgetSheetOptionsManager = () => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEtapa, setSelectedEtapa] = useState('ALL');
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [presetSearchTerm, setPresetSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    nome_opcao: '',
    descricao: '',
    weight_percentage: 0,
    fixed_cost: 0,
    ativo: true
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  // Agrupamento por Etapas da Ficha de Orçamento (7 Cards Técnicos)
  const etapasFicha = {
    CARD_1: {
      nome: '1. Cores e MDF',
      icon: Palette,
      color: 'text-emerald-700 bg-emerald-50 border-emerald-200',
      tipos: ['cor_interna', 'cor_externa', 'cor_moveis']
    },
    CARD_2: {
      nome: '2. Portas, Puxadores e Caixaria',
      icon: DoorOpen,
      color: 'text-blue-700 bg-blue-50 border-blue-200',
      tipos: [
        'modelo_portas', 'modelo_acabamento_portas', 'modelo_portas_mov',
        'modelo_puxadores', 'tipo_puxador_externo', 'puxador_embutido',
        'corpo_gavetas', 'tipo_fundo', 'corpo_armario', 'modelo_corpo_armario',
        'cor_ferragem'
      ]
    },
    CARD_3: {
      nome: '3. LED e Acabamento Inferior',
      icon: Lightbulb,
      color: 'text-amber-700 bg-amber-50 border-amber-200',
      tipos: ['led_tipo', 'led_cor', 'led_posicao', 'canaleta_led', 'acabamento_inferior']
    },
    CARD_4: {
      nome: '4. Portas de Vidro / Reflecta',
      icon: Sliders,
      color: 'text-cyan-700 bg-cyan-50 border-cyan-200',
      tipos: ['modelo_vidro', 'puxador_vidro', 'cor_armacao']
    },
    CARD_5: {
      nome: '5. Acessórios e Cabideiros',
      icon: Layers,
      color: 'text-indigo-700 bg-indigo-50 border-indigo-200',
      tipos: ['modelo_cabide', 'paineis_parede', 'tampo', 'pia_tipo', 'misturador', 'profundidade', 'box', 'chuveiro', 'registro', 'ralo']
    },
    CARD_6: {
      nome: '6. Eletros e Itens Embutidos',
      icon: Box,
      color: 'text-purple-700 bg-purple-50 border-purple-200',
      tipos: ['itens_embutidos']
    }
  };

  const tipoLabels = {
    // Cores
    cor_interna: 'Cor Interna (Caixaria)',
    cor_externa: 'Cor Externa (Frentes)',
    cor_moveis: 'Padrão Geral de MDF',
    // Portas
    modelo_portas: 'Modelo das Portas',
    modelo_acabamento_portas: 'Acabamento das Portas (Provençal, Reta, etc.)',
    modelo_portas_mov: 'Abertura das Portas (Abrir / Correr / Basculante)',
    // Puxadores
    modelo_puxadores: 'Modelo de Puxador',
    tipo_puxador_externo: 'Puxador Externo',
    puxador_embutido: 'Puxador Embutido / Perfil',
    puxador_vidro: 'Puxador de Porta de Vidro',
    cor_armacao: 'Cor da Armação / Alumínio',
    // Corpo / Caixaria
    corpo_gavetas: 'Corpo das Gavetas / Corrediças',
    tipo_fundo: 'Tipo e Espessura do Fundo',
    corpo_armario: 'Estrutura da Caixaria (15mm, 18mm, Engrossado)',
    modelo_corpo_armario: 'Modelo do Armário',
    // LED
    led_tipo: 'Tipo de Iluminação / LED',
    led_cor: 'Tonalidade do LED (Quente, Neutro, Frio)',
    led_posicao: 'Posição do Perfil de LED',
    canaleta_led: 'Tipo de Perfil / Canaleta de LED',
    // Acabamentos
    acabamento_inferior: 'Acabamento Inferior (Pedra, Inox, Suspenso, Rodízio)',
    // Vidro
    modelo_vidro: 'Tipo de Vidro (Reflecta Bronze, Fumê, Incolor, Canelado)',
    // Acessórios
    modelo_cabide: 'Modelo do Tubo Cabideiro (Comum, Iluminado, Extensivo)',
    paineis_parede: 'Painéis Ripados / Parede',
    cor_ferragem: 'Acabamento das Ferragens',
    tampo: 'Tampo / Bancada',
    pia_tipo: 'Tipo de Cuba / Pia',
    misturador: 'Misturador / Torneira',
    profundidade: 'Profundidade Especial',
    box: 'Box de Banheiro',
    chuveiro: 'Chuveiro',
    registro: 'Registro',
    ralo: 'Ralo',
    // Eletros
    itens_embutidos: 'Eletro / Item Embutido Cadastrado'
  };

  const [selectedTipo, setSelectedTipo] = useState('cor_interna');

  // Filtragem dinâmica de categorias pela Etapa selecionada
  const tiposDisponiveis = useMemo(() => {
    if (selectedEtapa === 'ALL') {
      return Object.keys(tipoLabels);
    }
    return etapasFicha[selectedEtapa]?.tipos || Object.keys(tipoLabels);
  }, [selectedEtapa]);

  useEffect(() => {
    if (tiposDisponiveis.length > 0 && !tiposDisponiveis.includes(selectedTipo)) {
      setSelectedTipo(tiposDisponiveis[0]);
    }
  }, [selectedEtapa, tiposDisponiveis, selectedTipo]);

  useEffect(() => {
    if (selectedTipo) {
      loadOptions(selectedTipo);
    }
  }, [selectedTipo]);

  const loadOptions = async (tipo) => {
    try {
      setLoading(true);
      const response = await budgetSheetOptionsService.getByTipo(tipo);
      if (response.success) {
        setOptions(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
      toast.error('Erro ao carregar opções de orçamento');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (option = null) => {
    setPresetSearchTerm('');
    if (option) {
      setEditingOption(option);
      setFormData({
        nome_opcao: option.nome_opcao,
        descricao: option.descricao || '',
        weight_percentage: parseFloat(option.weight_percentage || 0),
        fixed_cost: parseFloat(option.fixed_cost || 0),
        ativo: option.ativo !== undefined ? option.ativo : true
      });
      // Verifica se a opção está no catálogo pré-definido da ficha
      const presets = FICHA_TECNICA_PRESET_OPTIONS[selectedTipo] || [];
      const existsInPresets = presets.some(p => p.label.toLowerCase() === (option.nome_opcao || '').toLowerCase());
      setIsCustomMode(!existsInPresets);
    } else {
      setEditingOption(null);
      setIsCustomMode(false);
      setFormData({
        nome_opcao: '',
        descricao: '',
        weight_percentage: 0,
        fixed_cost: 0,
        ativo: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOption(null);
    setIsCustomMode(false);
    setPresetSearchTerm('');
    setFormData({ nome_opcao: '', descricao: '', weight_percentage: 0, fixed_cost: 0, ativo: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        ...formData,
        orcamento_tipo: selectedTipo,
        weight_percentage: parseFloat(formData.weight_percentage) || 0,
        fixed_cost: parseFloat(formData.fixed_cost) || 0
      };

      let response;
      if (editingOption) {
        response = await budgetSheetOptionsService.update(editingOption.id, data);
      } else {
        response = await budgetSheetOptionsService.create(data);
      }

      if (response.success) {
        toast.success(editingOption ? 'Opção e pesos atualizados!' : 'Nova opção de orçamento criada!');
        handleCloseModal();
        loadOptions(selectedTipo);
      } else {
        toast.error(response.message || 'Erro ao salvar opção');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar configuração da opção');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmMessage('Tem certeza que deseja excluir esta opção? Esta ação removerá os pesos associados.');
    setConfirmAction(() => async () => {
      try {
        setSaving(true);
        const response = await budgetSheetOptionsService.delete(id);
        if (response.success) {
          toast.success('Opção excluída!');
          loadOptions(selectedTipo);
        } else {
          toast.error(response.message || 'Erro ao excluir');
        }
      } catch (error) {
        console.error('Erro ao excluir:', error);
        toast.error('Erro ao excluir opção');
      } finally {
        setSaving(false);
      }
    });
    setShowConfirmModal(true);
  };

  const executeConfirmedAction = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
      setConfirmAction(null);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  };

  // Filtragem de busca por texto na tela
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt =>
      (opt.nome_opcao && opt.nome_opcao.toLowerCase().includes(term)) ||
      (opt.descricao && opt.descricao.toLowerCase().includes(term))
    );
  }, [options, searchTerm]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header com Contexto */}
      <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="p-3 bg-emerald-50 rounded-2xl text-[#2f6626] border border-emerald-100 shadow-xs">
            <Settings className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                Central de Pesos & Precificação da Ficha
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                Orçamento Automatizado
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              Configure os acréscimos percentuais (%) e adicionais fixos (R$) de cada especificação técnica preenchida no levantamento
            </p>
          </div>
        </div>

        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#49943d] hover:bg-[#3a7630] text-white rounded-xl text-sm font-bold shadow-sm hover:shadow transition-all"
        >
          <Plus className="w-4 h-4" />
          <span>Nova Opção com Peso</span>
        </button>
      </div>

      {/* Seletor Rápido de Etapas da Ficha */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
        <button
          type="button"
          onClick={() => setSelectedEtapa('ALL')}
          className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
            selectedEtapa === 'ALL'
              ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <Filter className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-wider">Todas</span>
          </div>
          <p className="text-xs font-bold leading-tight">Todas as Etapas</p>
        </button>

        {Object.entries(etapasFicha).map(([key, etapa]) => {
          const Icon = etapa.icon;
          const isSelected = selectedEtapa === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedEtapa(key)}
              className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                isSelected
                  ? 'bg-emerald-700 text-white border-emerald-700 shadow-sm ring-2 ring-emerald-500/20'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-emerald-600'}`} />
                <span className="text-[9px] font-semibold opacity-75">{etapa.tipos.length} tipos</span>
              </div>
              <p className="text-xs font-bold leading-tight truncate">{etapa.nome}</p>
            </button>
          );
        })}
      </div>

      {/* Área de Trabalho: Dropdown de Categoria + Busca + Tabela de Pesos */}
      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
        {/* Barra de Filtro de Categoria e Pesquisa */}
        <div className="p-4 sm:p-5 bg-slate-50/70 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Especificação Técnica da Ficha:
            </label>
            <select
              value={selectedTipo}
              onChange={(e) => setSelectedTipo(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-semibold text-gray-800 focus:ring-2 focus:ring-emerald-500 shadow-2xs"
            >
              {tiposDisponiveis.map(tipo => (
                <option key={tipo} value={tipo}>
                  {tipoLabels[tipo] || tipo}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full sm:w-72">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Filtrar nesta etapa:
            </label>
            <input
              type="text"
              placeholder="Buscar por nome ou detalhe..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500 shadow-2xs"
            />
          </div>
        </div>

        {/* Tabela de Opções e Pesos */}
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <span>{tipoLabels[selectedTipo] || selectedTipo}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                  {filteredOptions.length} opção(ões)
                </span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Valores configurados nesta opção serão somados automaticamente ao gerar o orçamento do projeto
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 gap-2">
              <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs">Carregando especificações e pesos...</span>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
              <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-700">Nenhuma opção cadastrada para esta especificação.</p>
              <p className="text-xs text-gray-400 mt-1">Clique no botão "Nova Opção com Peso" acima para começar a precificar esta etapa.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {filteredOptions.map((option) => {
                const weight = parseFloat(option.weight_percentage || 0);
                const fixed = parseFloat(option.fixed_cost || 0);
                const hasImpact = weight !== 0 || fixed !== 0;

                return (
                  <div
                    key={option.id}
                    className={`p-4 rounded-xl border transition-all relative overflow-hidden flex flex-col justify-between ${
                      !option.ativo
                        ? 'bg-gray-50/80 border-gray-200 opacity-60'
                        : hasImpact
                        ? 'bg-white border-emerald-300 shadow-2xs hover:shadow-sm'
                        : 'bg-white border-gray-200 hover:border-gray-300 shadow-2xs'
                    }`}
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h4 className={`text-sm font-bold ${option.ativo ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                          {option.nome_opcao}
                        </h4>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          option.ativo ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}>
                          {option.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>

                      {option.descricao && (
                        <p className="text-xs text-gray-500 mb-3 line-clamp-2 leading-relaxed">
                          {option.descricao}
                        </p>
                      )}

                      {/* Badges de Peso e Acréscimo */}
                      <div className="flex flex-wrap items-center gap-1.5 pt-1 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${
                          weight > 0
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : weight < 0
                            ? 'bg-rose-50 text-rose-800 border border-rose-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}>
                          <Percent className="w-3 h-3" />
                          <span>{weight > 0 ? `+${weight}%` : `${weight}%`}</span>
                        </span>

                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${
                          fixed > 0
                            ? 'bg-blue-50 text-blue-800 border border-blue-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}>
                          <DollarSign className="w-3 h-3" />
                          <span>{fixed > 0 ? `+${formatCurrency(fixed)}` : 'R$ 0,00'}</span>
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-2">
                      <span className="text-[11px] text-gray-400 font-medium">ID #{option.id}</span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenModal(option)}
                          className="px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors flex items-center gap-1"
                          title="Editar Opção e Pesos"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          <span>Configurar</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(option.id)}
                          className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Excluir Opção"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Criação/Edição Completo com Pesos */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-slate-50/60">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-100/70 text-emerald-800 rounded-xl">
                  <Sliders className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {editingOption ? 'Editar Especificação e Pesos' : 'Nova Especificação com Precificação'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Etapa: <strong className="text-gray-800">{tipoLabels[selectedTipo] || selectedTipo}</strong>
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Seletor Inteligente de Opções Oficiais da Ficha Técnica vs Modo Personalizado */}
              {(() => {
                const availablePresets = FICHA_TECNICA_PRESET_OPTIONS[selectedTipo] || [];
                const filteredPresets = availablePresets.filter(p =>
                  p.label.toLowerCase().includes(presetSearchTerm.toLowerCase()) ||
                  (p.desc && p.desc.toLowerCase().includes(presetSearchTerm.toLowerCase()))
                );

                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Opção Técnica da Ficha *
                      </label>
                      {availablePresets.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsCustomMode(!isCustomMode);
                            if (isCustomMode && availablePresets.length > 0) {
                              setFormData(prev => ({ ...prev, nome_opcao: availablePresets[0].label, descricao: availablePresets[0].desc || '' }));
                            }
                          }}
                          className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline"
                        >
                          {isCustomMode ? '← Escolher da lista oficial' : 'Ou digitar opção personalizada'}
                        </button>
                      )}
                    </div>

                    {!isCustomMode && availablePresets.length > 0 ? (
                      <div className="space-y-2">
                        {availablePresets.length > 8 && (
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              value={presetSearchTerm}
                              onChange={(e) => setPresetSearchTerm(e.target.value)}
                              placeholder="Filtrar opções desta categoria..."
                              className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-emerald-500"
                            />
                          </div>
                        )}

                        <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto p-1.5 border border-gray-200 rounded-xl bg-gray-50/50">
                          {filteredPresets.map((preset) => {
                            const isSelected = (formData.nome_opcao || '').toLowerCase() === preset.label.toLowerCase();
                            return (
                              <button
                                key={preset.label}
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    nome_opcao: preset.label,
                                    descricao: preset.desc || formData.descricao || ''
                                  });
                                }}
                                className={`flex items-start justify-between p-2.5 rounded-lg text-left transition-all border ${
                                  isSelected
                                    ? 'bg-emerald-50 border-emerald-500 text-emerald-950 ring-1 ring-emerald-500 shadow-2xs font-semibold'
                                    : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-100 hover:border-gray-300'
                                }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs leading-tight">{preset.label}</p>
                                  {preset.desc && (
                                    <p className="text-[10px] text-gray-500 mt-0.5 truncate">{preset.desc}</p>
                                  )}
                                </div>
                                {isSelected && (
                                  <span className="p-0.5 bg-emerald-600 text-white rounded-full ml-2 shrink-0">
                                    <Check className="w-3 h-3" />
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="text"
                          value={formData.nome_opcao}
                          onChange={(e) => setFormData({ ...formData, nome_opcao: e.target.value })}
                          placeholder="Ex: Cava Usinada 45°, Fita LED 2700K com Canaleta, Acabamento em Pedra..."
                          className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 font-medium"
                          required
                        />
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Descrição / Detalhamento de Fábrica (Opcional)
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows={2}
                  placeholder="Ex: Requer perfil de alumínio modelo slim e usinagem frontal..."
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              {/* Bloco de Precificação Automatizada */}
              <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200/80 space-y-3">
                <div className="flex items-center gap-1.5 text-emerald-900">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                  <span className="text-xs font-bold uppercase tracking-wider">Regra de Precificação no Orçamento</span>
                </div>
                <p className="text-[11px] text-emerald-800 leading-snug">
                  Quando o projetista marcar esta opção na Ficha Técnica, o orçamento automatizado aplicará esses acréscimos automaticamente.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className="block text-xs font-bold text-emerald-950 mb-1">
                      Acréscimo Percentual (%)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={formData.weight_percentage}
                        onChange={(e) => setFormData({ ...formData, weight_percentage: e.target.value })}
                        className="w-full pl-3 pr-8 py-2 bg-white border border-emerald-300 rounded-lg text-sm font-bold text-emerald-950 focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-emerald-700">%</span>
                    </div>
                    <span className="text-[10px] text-emerald-700">Ex: 5 para +5% sobre a base</span>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-emerald-950 mb-1">
                      Acréscimo Fixo em Reais (R$)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-emerald-700">R$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.fixed_cost}
                        onChange={(e) => setFormData({ ...formData, fixed_cost: e.target.value })}
                        className="w-full pl-9 pr-3 py-2 bg-white border border-emerald-300 rounded-lg text-sm font-bold text-emerald-950 focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                    <span className="text-[10px] text-emerald-700">Ex: 150 para +R$ 150,00 fixos</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all">
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
                  />
                  <div>
                    <span className="text-xs font-bold text-gray-800">Opção Ativa no Formulário</span>
                    <p className="text-[11px] text-gray-500">Se desmarcada, não aparecerá para seleção nas novas fichas técnicas.</p>
                  </div>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={saving}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 text-xs font-bold text-white bg-[#49943d] hover:bg-[#3a7630] rounded-xl shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Salvando...' : 'Salvar Especificação'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={executeConfirmedAction}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};

export default BudgetSheetOptionsManager;
